import React from 'react';
import { useNavigate } from 'react-router-dom';
import ReactToPrint from 'react-to-print';
import classNames from 'classnames';
import { withAITracking } from '@microsoft/applicationinsights-react-js';

import { reactPlugin } from 'app-insights';

import NotFound from 'pages/NotFound';

import Form from 'components/Form';
import { Loader, Warning } from 'components/Base';
import { BackButton, Button } from 'components/Buttons';

import { useCreditScoreDisclosureData } from 'utils/useCreditScoreDisclosureData';

import { env } from 'env';

import styles from './CreditScoreDisclosure.module.scss';

const CreditScoreDisclosure = () => {
  const navigate = useNavigate();
  const printableRef = React.useRef<HTMLDivElement>(null);
  const { applicationLoading, borrowerLoading, creditScoreDisclosureData } = useCreditScoreDisclosureData();

  if (applicationLoading || borrowerLoading) {
    return <Loader />;
  }

  if (!creditScoreDisclosureData) {
    return <NotFound />;
  }

  const { name, completeDate, score } = creditScoreDisclosureData;

  const renderFormFooter = () => (
    <div className="buttons">
      <BackButton onClick={() => navigate(`${env.REACT_APP_BASE_URL}/`)}>Back</BackButton>
    </div>
  );

  const getFormattedDate = () => {
    if (!completeDate) {
      return null;
    }

    return `${completeDate.getMonth() + 1}/${completeDate.getDate()}/${completeDate.getFullYear()}`;
  };

  return (
    <>
      <Form title="Credit Score Disclosure Notice" renderFooter={renderFormFooter}>
        <Warning className={styles.credit_score_disclosure_warning}>
          You may want to print or save this page for your records.
        </Warning>
        <div ref={printableRef}>
          <div className={styles.address_section}>
            <p className="subtitle_small">Republic Finance, LLC</p>
            <p className="caption">7031 Commerce Circle</p>
            <p className="caption">Baton Rouge LA 70809</p>
          </div>
          <div className={classNames('subtitle_medium', styles.subtitle)}>
            Your Credit Score and the Price You Pay For Credit
          </div>
          <div className={styles.borrower_score}>
            <p className="subtitle_medium">Your Credit Score</p>
            <div className={styles.section_detail}>
              <div className={styles.section_column}>
                <p className={classNames('text_small', styles.score_field)}>Name: {name}</p>
                <p className={classNames('text_small', styles.score_field)}>FICO Score 8 based on Equifax Data - F</p>
              </div>
              <div className={styles.section_column}>
                <p className={classNames('text_small', styles.score_field)}>Credit Score: {score}</p>
                <p className={classNames('text_small', styles.score_field)}>
                  Score Creation Date: {getFormattedDate()}
                </p>
              </div>
            </div>
            <p className="text_small">Source: Equifax Information Services LLC</p>
          </div>
          <div className={classNames('subtitle_medium', styles.subtitle)}>Understanding Your Credit Score</div>
          <div className={styles.info_section}>
            <div className={classNames('subtitle_small', styles.section_subtitle)}>
              What you should know about credit scores
            </div>
            <p className="text_small">
              Your credit score is a number that reflects the information in your credit report.
            </p>
            <p className="text_small">
              Your credit report is a record of your credit history. It includes information about whether you pay your
              bills on time and how much you owe to creditors.
            </p>
            <p className="text_small">Your credit score can change, depending on how your credit history changes.</p>
          </div>
          <div className={styles.info_section}>
            <div className={classNames('subtitle_small', styles.section_subtitle)}>How we use your credit score</div>
            <p className="text_small">
              Your credit score can affect whether you can get a loan and how much you will have to pay for that loan.
            </p>
          </div>
          <div className={styles.info_section}>
            <div className={classNames('subtitle_small', styles.section_subtitle)}>The range of scores</div>
            <p className="text_small">Overall score range for FICO Score 8 based on Equifax Data - F: 300-850.</p>
            <p className="text_small">
              Generally, the higher your score, the more likely you are to be offered better credit terms.
            </p>
          </div>
          <div className={styles.info_section}>
            <div className={classNames('subtitle_small', styles.section_subtitle)}>
              How your score compares to the scores of other consumers
            </div>
            <div className={styles.fico_image} />
          </div>
          <div className={styles.info_section}>
            <div className={classNames('subtitle_small', styles.section_subtitle)}>
              What if there are mistakes in your credit report?
            </div>
            <p className="text_small">
              You have a right to dispute any inaccurate information in your credit report. If you find mistakes on your{' '}
              credit report, contact the consumer reporting agency.
            </p>
            <p className="text_small">
              It is a good idea to check your credit report to make sure the information it contains is accurate.
            </p>
          </div>
          <div className={styles.info_section}>
            <div className={classNames('subtitle_small', styles.section_subtitle)}>
              How can you obtain a copy of your credit report?
            </div>
            <p className="text_small">
              Under Federal law, you have the right to obtain a free copy of your credit report from each of the
              nationwide consumer reporting agencies once a year.
            </p>
            <p className="text_small">To order your free annual credit report –</p>
            <div className={styles.section_detail}>
              <div className={styles.section_column}>
                <p className="caption">By Telephone:</p>
                <p className="caption">On the web:</p>
                <p className="caption">By mail:</p>
              </div>
              <div className={styles.section_column}>
                <p className="caption">Call toll-free: 1-877-322-8228</p>
                <p className="caption">
                  Visit{' '}
                  <a
                    href="https://www.annualcreditreport.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                  >
                    www.annualcreditreport.com
                  </a>
                </p>
                <p className="caption">
                  Mail your completed Annual Credit Report Request Form (which you can obtain from the Federal Trade
                  Commission's web site at{' '}
                  <a
                    href="http://www.ftc.gov/bcp/conline/include/requestformfinal.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                  >
                    http://www.ftc.gov/bcp/conline/include/requestformfinal.pdf
                  </a>
                  ) to: Annual Credit Report Request Service P.O. Box 105281 Atlanta, GA 30348-5281
                </p>
              </div>
            </div>
          </div>
          <div className={styles.info_section}>
            <div className={classNames('subtitle_small', styles.section_subtitle)}>
              How can you get more information?
            </div>
            <p className="text_small">
              For more information about credit reports and your rights under Federal law, visit the Consumer Financial
              Protection Bureau's web site at{' '}
              <a
                href="https://www.consumerfinance.gov/learnmore"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                www.consumerfinance.gov/learnmore
              </a>
              .
            </p>
          </div>
        </div>
        <div className={styles.print_button}>
          <ReactToPrint trigger={() => <Button>Print</Button>} content={() => printableRef.current} />
        </div>
      </Form>
    </>
  );
};

export default withAITracking(reactPlugin, CreditScoreDisclosure, 'CreditScoreDisclosurePage');
